import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  CircularProgress, 
  Alert,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getLegacyTaskDetail, completeTask, submitTaskForm } from '../api/alfresco';
import { getTicket } from '../utils/auth';
import { ALFRESCO_API_URL, ALFRESCO_BASE_URL } from '../utils/constants';

const MAIN_PROPERTIES = ['bpm_comment', 'bpm_description', 'bpm_startDate', 'cm_created'];
const READONLY_PROPERTIES = ['bpm_startDate', 'cm_created'];

// Groupes de champs selon le modèle Alfresco
const FIELD_GROUPS = {
  // --------------------------
  // DÉROGATION / CIMA
  // --------------------------
  demandeDerogation: {
    title: 'Informations de la demande',
    icon: '📄',
    fields: [
      'cima_referenceDemande',
      'cima_dateReport',
      'cima_motif',
      'cima_typeAssurance'
    ]
  },

  // --------------------------
  // CIN — Informations personnelles
  // --------------------------
  cin: {
    title: 'Informations Personnelles (CIN)',
    icon: '👤',
    fields: [
      'biat_cin',
      'biat_nom',
      'biat_prenom',
      'biat_filiationPatronyme',
      'biat_nomComplet',
      'biat_dateNaissance',
      'biat_lieuNaissance',
      'biat_nomCompletMere',
      'biat_profession',
      'biat_adresseComplete',
      'biat_lieuDateDelivrance',
      'biat_typeCarte',
      'biat_empreinteDigitale',
      'biat_cachetOfficiel',
      'biat_codeBarresVisible'
    ]
  },

  // --------------------------
  // PERMIS DE CONDUIRE
  // --------------------------
  permis: {
    title: 'Permis de Conduire',
    icon: '🪪',
    fields: [
      'biat_numPermis',
      'biat_dateObtentionPermis',
      'biat_dateExpirationPermis',
      'biat_CategoriePermis',
      'biat_nomPermis',
      'biat_prenomPermis',
      'biat_dateNaissancePermis',
      'biat_lieuNaissancePermis',
      'biat_cinPermis',
      'biat_adressePermis',
      'biat_autoriteDélivrance',
      'biat_formatPermis'
    ]
  },

  // --------------------------
  // CARTE GRISE & VÉHICULE
  // --------------------------
  carteGrise: {
    title: 'Carte Grise & Véhicule',
    icon: '🚗',
    fields: [
      'biat_numcarteGrise',
      'biat_immatriculation',
      'biat_nomProprietaire',
      'biat_adresseProprietaire',
      'biat_cinProprietaire',
      'biat_activiteVehicule',
      'biat_categorieVehicule',
      'biat_vin',
      'biat_marque',
      'biat_model',
      'biat_type',
      'biat_dateMiseCirculation',
      'biat_typeCarrosserie',
      'biat_typeCarburant',
      'biat_puissanceFiscale',
      'biat_poidsVide',
      'biat_nombreEssieux',
      'biat_chargeUtile',
      'biat_nombreplaces',
      'biat_placesDebout',
      'biat_nombreports',
      'biat_cylindrie',
      'biat_ptacPtra',
      'biat_immatriculationPrecedente',
      'biat_numeroCertificat',
      'biat_dateCertificat',
      'biat_lieuCertificat',
      'biat_codeBarres',
      'biat_couleur',
      'biat_puissance'
    ]
  },

  // --------------------------
  // VISITE TECHNIQUE
  // --------------------------
  vt: {
    title: 'Visite Technique',
    icon: '🔧',
    fields: [
      'biat_dateObtentionVT',
      'biat_validiteVT'
    ]
  },

  // --------------------------
  // ASSURANCE
  // --------------------------
  assurance: {
    title: 'Assurance',
    icon: '🛡️',
    fields: [
      'biat_typeAssurance',
      'biat_DateSouhaitee'
    ]
  },

  // --------------------------
  // DOCUMENTS / WORKFLOW
  // --------------------------
  workflow: {
    title: 'Commentaires',
    icon: '💬',
    fields: [
      'cima_motif',
      'bpm_comment',
      'packageItems'
    ]
  },

  // --------------------------
  // AUTRES (fallback)
  // --------------------------
  autres: {
    title: 'Autres Propriétés',
    icon: '📋',
    fields: []
  }
};

const TaskDetail = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [formFields, setFormFields] = useState([]);
  const [groupedFields, setGroupedFields] = useState({});
  const [taskDetail, setTaskDetail] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(0);
  const selectedDocument = documents[selectedDocumentIndex] || null;
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [documentVersion, setDocumentVersion] = useState(0);

  useEffect(() => {
    loadTaskDetail();
  }, [taskId]);

  const extractNodeId = (nodeRef) => {
    if (!nodeRef) return null;
    // Extraire l'ID du nodeRef format: workspace://SpacesStore/nodeId
    const parts = nodeRef.split('/');
    return parts[parts.length - 1];
  };

  const loadPackageDocuments = async (packageNodeRef) => {
    try {
      const nodeId = extractNodeId(packageNodeRef);
      if (!nodeId) return [];

      const ticket = getTicket();
      const response = await fetch(
        `${ALFRESCO_API_URL}/alfresco/versions/1/nodes/${nodeId}/children?skipCount=0&maxItems=100&alf_ticket=${ticket}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const docs = data.list?.entries || [];
        // Extraire les objets entry de la structure de l'API publique
        const documents = docs.map(item => item.entry);
        setDocuments(documents);

        if (documents.length === 0) {
          setSelectedDocumentIndex(0);
        } else {
          const currentKey = selectedDocument?.nodeRef || selectedDocument?.id || null;
          if (currentKey) {
            const matchedIndex = documents.findIndex(doc => (doc.nodeRef || doc.id) === currentKey);
            setSelectedDocumentIndex(matchedIndex >= 0 ? matchedIndex : documents.length - 1);
          } else {
            setSelectedDocumentIndex(documents.length - 1);
          }
        }
        
        return documents;
      }
      return [];
    } catch (err) {
      console.error('Erreur lors du chargement des documents:', err);
      return [];
    }
  };

  const getDocumentPreviewUrl = (document) => {
    if (!document) return null;
    
    const nodeId = document.id || extractNodeId(document.nodeRef);
    const ticket = getTicket();
    
    // Toujours utiliser l'URL absolue pour les iframes (le proxy ne fonctionne pas pour les iframes)
    const baseUrl = process.env.REACT_APP_ALFRESCO_URL || 'https://ged.dev.addinn-group.com/alfresco';
    
    // URL pour la prévisualisation inline (sans téléchargement forcé)
    // Ajouter un paramètre de version pour forcer le rechargement
    return `${baseUrl}/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/content?attachment=false&v=${documentVersion}&alf_ticket=${ticket}`;
  };

  const getDocumentContentUrl = (document) => {
    if (!document) return null;
    
    const nodeId = document.id || extractNodeId(document.nodeRef);
    const ticket = getTicket();
    
    // Toujours utiliser l'URL absolue pour les liens de téléchargement
    const baseUrl = process.env.REACT_APP_ALFRESCO_URL || 'https://ged.dev.addinn-group.com/alfresco';
    
    // URL pour le contenu direct du document via l'API publique
    // Ajouter un paramètre de version pour forcer le rechargement
    return `${baseUrl}/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/content?v=${documentVersion}&alf_ticket=${ticket}`;
  };

  const handleDocumentNavigation = (direction) => {
    if (documents.length <= 1) return;
    
    setSelectedDocumentIndex((prevIndex) => {
      const total = documents.length;
      if (total === 0) return 0;
      if (direction === 'prev') {
        return (prevIndex - 1 + total) % total;
      }
      if (direction === 'next') {
        return (prevIndex + 1) % total;
      }
      return prevIndex;
    });
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setUploadFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setUploadFile(files[0]);
    }
  };

  const handleRemoveFile = () => {
    setUploadFile(null);
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setUploadFile(null);
    setIsDragging(false);
    setUploadSuccess(false);
    setUploadError('');
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedDocument) return;
    
    setUploading(true);
    setUploadError('');
    setUploadSuccess(false);
    
    try {
      const ticket = getTicket();
      // Construire le nodeRef à partir de l'ID du document
      const nodeId = selectedDocument.id || extractNodeId(selectedDocument.nodeRef);
      if (!nodeId) {
        throw new Error('Impossible de déterminer l\'identifiant du document');
      }
      const nodeRef = selectedDocument.nodeRef || `workspace://SpacesStore/${nodeId}`;
      
      // Créer FormData pour l'upload
      const formData = new FormData();
      formData.append('filedata', uploadFile);
      formData.append('filename', uploadFile.name);
      formData.append('destination', '');
      formData.append('createdirectory', 'true');
      formData.append('majorVersion', 'false');
      formData.append('username', '');
      formData.append('overwrite', 'true');
      formData.append('thumbnails', '');
      formData.append('updatenameandmimetype', 'false');
      formData.append('updateNodeRef', nodeRef);
      
      // Upload via l'API Alfresco
      const response = await fetch(
        `${ALFRESCO_BASE_URL}/s/api/upload?alf_ticket=${ticket}`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur lors de l'upload: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      // Recharger les documents après l'upload réussi
      const packageNodeRef = taskDetail?.properties?.bpm_package;
      if (packageNodeRef) {
        const updatedDocuments = await loadPackageDocuments(packageNodeRef);
        if (updatedDocuments && updatedDocuments.length > 0) {
          // Trouver l'index du document mis à jour par son nodeRef
          const updatedIndex = updatedDocuments.findIndex(doc => {
            const docNodeRef = doc.nodeRef || `workspace://SpacesStore/${doc.id}`;
            return docNodeRef === nodeRef;
          });
          if (updatedIndex >= 0) {
            setSelectedDocumentIndex(updatedIndex);
          } else {
            setSelectedDocumentIndex(updatedDocuments.length - 1);
          }
        }
      }
      
      setUploadSuccess(true);
      
      // Forcer le rechargement du visualiseur en incrémentant la version
      setDocumentVersion(prev => prev + 1);
      
      setTimeout(() => {
        handleCloseUploadDialog();
        setUploadSuccess(false);
      }, 1500);
    } catch (err) {
      setUploadError(err.message || 'Erreur lors de l\'upload du document');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const renderUploadDialog = () => {
    return (
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: 'primary.main',
          color: 'white'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Importer une nouvelle version
          </Typography>
          <IconButton
            onClick={handleCloseUploadDialog}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: `2px dashed ${isDragging ? 'primary.main' : '#ccc'}`,
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: isDragging ? 'primary.light' : '#fafafa',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'primary.light'
              }
            }}
            onClick={() => document.getElementById('file-input').click()}
          >
            <input
              id="file-input"
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
            
            {uploadFile ? (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Nouvelle version sélectionnée
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: 2,
                  mt: 2,
                  p: 2,
                  backgroundColor: 'white',
                  borderRadius: 1,
                  border: '1px solid #ddd'
                }}>
                  <Typography variant="body1" sx={{ flexGrow: 1, textAlign: 'left' }}>
                    {uploadFile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile();
                    }}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Cliquez pour sélectionner un autre fichier ou glissez-déposez une nouvelle version
                </Typography>
              </Box>
            ) : (
              <Box>
                <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Glissez-déposez la nouvelle version ici
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  ou cliquez pour sélectionner un fichier
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Formats acceptés: PDF, DOC, DOCX, JPG, JPEG, PNG
                </Typography>
              </Box>
            )}
          </Box>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                Upload en cours...
              </Typography>
            </Box>
          )}

          {uploadSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Document mis à jour avec succès !
            </Alert>
          )}

          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseUploadDialog}
            disabled={uploading}
            color="primary"
            variant="text"
          >
            Annuler
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!uploadFile || uploading}
            color="primary"
          >
            {uploading ? <CircularProgress size={24} color="inherit" /> : 'Uploader'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const buildLabel = (key) => {
    if (!key) return '';
    const cleanKey = key.replace(/^cima[_:]/i, '').replace(/^biat[_:]/i, '').replace(/^bpm[_:]/i, '').replace(/^cm[_:]/i, '');
    return cleanKey
      .replace(/([A-Z])/g, ' $1')
      .replace(/[_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^\w/, (c) => c.toUpperCase());
  };

  const detectFieldType = (key) => {
    if (!key) return 'text';
    if (key.toLowerCase().includes('description') || key.toLowerCase().includes('comment')) {
      return 'textarea';
    }
    if (key.toLowerCase().includes('date')) {
      return 'date';
    }
    return 'text';
  };

  const buildFormFields = (detail) => {
    const properties = detail?.properties || {};
    const propertyLabels = detail?.propertyLabels || {};

    const customFields = Object.keys(properties)
      .filter((key) => key.startsWith('cima_') || key.startsWith('biat_'))
      .map((key) => ({
        id: key,
        label: propertyLabels[key] || buildLabel(key),
        type: detectFieldType(key),
        readOnly: false
      }));

    const mainFields = MAIN_PROPERTIES.map((key) => ({
      id: key,
      label: propertyLabels[key] || buildLabel(key),
      type: detectFieldType(key),
      readOnly: READONLY_PROPERTIES.includes(key)
    }));

    const fieldMap = new Map();
    [...mainFields, ...customFields].forEach((field) => {
      if (!fieldMap.has(field.id)) {
        fieldMap.set(field.id, field);
      }
    });

    return Array.from(fieldMap.values());
  };

  const groupFieldsByCategory = (fields) => {
    const groupedFields = {};
    
    // Initialiser tous les groupes
    Object.keys(FIELD_GROUPS).forEach(groupKey => {
      groupedFields[groupKey] = [];
    });

    // Filtrer les champs pour exclure les propriétés outcome et autres propriétés système
    const filteredFields = fields.filter(field => {
      const fieldId = field.id.toLowerCase();
      return !fieldId.includes('outcome') && 
             !fieldId.includes('valider') &&
             !fieldId.startsWith('bpm_') &&
             !fieldId.startsWith('cm_') ||
             fieldId === 'bpm_comment'; // Garder seulement bpm_comment
    });

    // Classer les champs dans leurs groupes respectifs
    filteredFields.forEach(field => {
      let assigned = false;
      
      // Chercher dans quel groupe appartient ce champ
      Object.keys(FIELD_GROUPS).forEach(groupKey => {
        if (FIELD_GROUPS[groupKey].fields.includes(field.id)) {
          groupedFields[groupKey].push(field);
          assigned = true;
        }
      });
      
      // Si le champ n'appartient à aucun groupe prédéfini, l'ajouter aux "autres"
      if (!assigned) {
        groupedFields.autres.push(field);
      }
    });

    // Supprimer les groupes vides
    Object.keys(groupedFields).forEach(groupKey => {
      if (groupedFields[groupKey].length === 0) {
        delete groupedFields[groupKey];
      }
    });

    return groupedFields;
  };

  const loadTaskDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const detail = await getLegacyTaskDetail(taskId, { detailed: true });
      setTaskDetail(detail);

      const generatedFields = buildFormFields(detail);
      setFormFields(generatedFields);
      
      const grouped = groupFieldsByCategory(generatedFields);
      setGroupedFields(grouped);

      const properties = detail?.properties || {};
      const initialData = {};

      generatedFields.forEach((field) => {
        const value = properties[field.id];
        if (value === null || value === undefined) {
          initialData[field.id] = '';
        } else if (Array.isArray(value)) {
          initialData[field.id] = value.join(', ');
        } else if (typeof value === 'object') {
          initialData[field.id] = JSON.stringify(value);
        } else {
          initialData[field.id] = value;
        }
      });
      setFormData(initialData);

      // Charger les documents du package
      const packageNodeRef = detail?.properties?.bpm_package;
      if (packageNodeRef) {
        await loadPackageDocuments(packageNodeRef);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        'Erreur lors du chargement du formulaire'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const getTaskType = () => {
    if (!taskDetail) return null;
    
    const taskName = taskDetail.name?.toLowerCase() || '';
    const taskTitle = taskDetail.title?.toLowerCase() || '';
    const properties = taskDetail.properties || {};
    
    // Vérifier si c'est une tâche de validation
    if (taskName.includes('validation') || taskTitle.includes('validation') || properties.cima_validationOutcome) {
      return 'validation';
    }
    
    // Vérifier si c'est une tâche de rectification par le demandeur (avec outcome "Rectifier")
    if (taskName.includes('rectificationdemandeur') || taskTitle.includes('rectification des pièces justificatifs') || properties.cima_rectificationDemandeurOutcome) {
      return 'rectificationDemandeur';
    }
    
    // Vérifier si c'est une autre tâche de rectification
    if (taskName.includes('rectif') || taskName.includes('correct') || taskTitle.includes('rectif')) {
      return 'rectification';
    }
    
    // Vérifier si c'est une tâche "Demande de dérogation"
    if (
      taskName.includes('demande') &&
      taskName.includes('derog') ||
      taskTitle.includes('demande de dérogation') ||
      properties.cima_demandeDerogationOutcome
    ) {
      return 'demandeDerogation';
    }

    // Vérifier si c'est une tâche d'élaboration de devis
    if (
      taskName.includes('devis') ||
      taskName.includes('elabor') ||
      taskTitle.includes('devis') ||
      properties.cima_elaborationDevisOutcome
    ) {
      return 'devis';
    }
    
    return 'default';
  };

  const getTaskButtons = () => {
    const taskType = getTaskType();
    
    switch (taskType) {
      case 'validation':
        return [
          { label: 'Valider', color: 'success', outcome: 'Valider' },
          { label: 'Incomplet', color: 'error', outcome: 'Incomplet' }
        ];
      case 'rectificationDemandeur':
        return [
          { label: 'Rectifier', color: 'warning', outcome: 'Rectifier' }
        ];
      case 'rectification':
        return [
          { label: 'Incomplet', color: 'error', outcome: 'Incomplet' }
        ];
      case 'devis':
        return [
          { label: 'Accepté', color: 'success', outcome: 'Accepte' },
          { label: 'Dérogation', color: 'warning', outcome: 'Derogation' },
          { label: 'Sans Suite', color: 'error', outcome: 'SansSuite' }
        ];
      case 'demandeDerogation':
        return [
          { label: 'Terminer', color: 'primary', outcome: 'Terminer' }
        ];
      default:
        return [
          { label: 'Soumettre', color: 'primary', outcome: 'Soumettre' }
        ];
    }
  };

  const handleSubmit = async (e, outcome = null) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Fonction helper pour convertir les valeurs en format compatible Alfresco
      const convertValue = (value) => {
        if (value === null || value === undefined) {
          return null;
        }
        // Convertir les tableaux en chaînes de caractères
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        // Convertir les objets en chaînes JSON
        if (typeof value === 'object') {
          return JSON.stringify(value);
        }
        // Retourner la valeur telle quelle (string, number, boolean)
        return value;
      };

      // Construire le payload au format Alfresco
      const payload = {};
      const properties = taskDetail?.properties || {};
      
      // Propriétés système à exclure (ne doivent pas être envoyées dans le formulaire)
      const excludedProperties = [
        'bpm_pooledActors',
        'bpm_hiddenTransitions',
        'bpm_completedItems',
        'bpm_package',
        'bpm_context',
        'bpm_taskId',
        'cm_content',
        'cm_owner'
      ];
      
      // Ajouter toutes les propriétés du formulaire avec le préfixe prop_
      // Inclure aussi les propriétés initiales qui n'ont pas été modifiées
      Object.keys(properties).forEach(key => {
        // Ignorer les propriétés système exclues
        if (excludedProperties.includes(key)) {
          return;
        }
        
        const value = formData[key] !== undefined ? formData[key] : properties[key];
        // Inclure les valeurs même si elles sont des chaînes vides
        if (value !== null && value !== undefined) {
          // Convertir les clés au format prop_ et convertir les valeurs
          const convertedValue = convertValue(value);
          if (convertedValue !== null) {
            payload[`prop_${key}`] = convertedValue;
          }
        }
      });
      
      // Ajouter aussi les propriétés du formulaire qui ne sont pas dans les propriétés initiales
      Object.keys(formData).forEach(key => {
        if (!properties.hasOwnProperty(key) && formData[key] !== null && formData[key] !== undefined) {
          const convertedValue = convertValue(formData[key]);
          if (convertedValue !== null) {
            payload[`prop_${key}`] = convertedValue;
          }
        }
      });
      
      // Ajouter l'outcome de validation si spécifié
      if (outcome) {
        const taskType = getTaskType();
        if (taskType === 'validation') {
          payload['prop_cima_validationOutcome'] = outcome;
        } else if (taskType === 'rectificationDemandeur') {
          payload['prop_cima_rectificationDemandeurOutcome'] = outcome;
        } else if (taskType === 'devis') {
          payload['prop_cima_devisOutcome'] = outcome;
        } else if (taskType === 'demandeDerogation') {
          payload['prop_cima_derogationOutcome'] = outcome;
        } else {
          // Pour les autres types de tâches, trouver la propriété outcome appropriée
          const outcomeProperty = Object.keys(properties).find(key => 
            key.toLowerCase().includes('outcome')
          );
          
          if (outcomeProperty) {
            payload[`prop_${outcomeProperty}`] = outcome;
          }
        }
      }
      
      // Ajouter la transition (toujours "Next")
      payload['prop_transitions'] = 'Next';
      
      // Ajouter les associations de package (vides)
      payload['assoc_packageItems_added'] = '';
      payload['assoc_packageItems_removed'] = '';
      
      await submitTaskForm(taskId, payload);
      navigate('/tasks');
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        'Erreur lors de la soumission du formulaire'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.id] ?? '';
    const commonProps = {
      fullWidth: true,
      label: field.label,
      value: field.type === 'date' && value ? formatDate(value) : value,
      onChange: (e) => handleChange(field.id, e.target.value),
      disabled: false,
      margin: 'normal'
    };

    if (field.type === 'textarea') {
      return <TextField key={field.id} {...commonProps} multiline rows={4} />;
    }

    return <TextField key={field.id} {...commonProps} />;
  };

  const renderCommentHistory = () => {
    const historyRaw = formData?.cima_motif || taskDetail?.properties?.cima_motif;
    if (!historyRaw || !historyRaw.trim()) {
      return null;
    }

    const historyEntries = historyRaw
      .split(/\n{2,}/)
      .map((entry) => entry.trim())
      .filter(Boolean);

    if (historyEntries.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
          Historique des commentaires
        </Typography>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'grey.50',
            p: 2,
            maxHeight: 240,
            overflowY: 'auto'
          }}
        >
          {historyEntries.map((entry, index) => (
            <Box
              key={index}
              sx={{
                mb: index !== historyEntries.length - 1 ? 2 : 0,
                pb: index !== historyEntries.length - 1 ? 2 : 0,
                borderBottom: index !== historyEntries.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider'
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                {entry}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const formatDate = (value) => {
    if (!value) return '';
    try {
      return format(new Date(value), 'dd/MM/yyyy', { locale: fr });
    } catch {
      return value;
    }
  };

  const renderHeaderInfo = () => {
    if (!taskDetail) return null;
    const status =
      taskDetail.propertyLabels?.bpm_status || taskDetail.properties?.bpm_status || taskDetail.state;
    const priority = taskDetail.propertyLabels?.bpm_priority || taskDetail.properties?.bpm_priority || '—';
    
    const getStatusColor = (status) => {
      if (!status) return 'default';
      const statusLower = status.toLowerCase();
      if (statusLower.includes('termin') || statusLower.includes('complet')) return 'success';
      if (statusLower.includes('progress') || statusLower.includes('cours')) return 'info';
      if (statusLower.includes('attent') || statusLower.includes('pending')) return 'warning';
      return 'default';
    };

    const getPriorityColor = (priority) => {
      if (!priority) return 'text.secondary';
      const priorityLower = priority.toLowerCase();
      if (priorityLower.includes('haute') || priorityLower.includes('high')) return 'error.main';
      if (priorityLower.includes('moyenne') || priorityLower.includes('medium')) return 'warning.main';
      if (priorityLower.includes('basse') || priorityLower.includes('low')) return 'success.main';
      return 'text.secondary';
    };
    
    return (
      <Box sx={{ mb: 3 }}>
        {/* Titre principal */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            mb: 1,
            color: 'primary.main'
          }}>
            {taskDetail.title || taskDetail.name}
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.6
          }}>
            {taskDetail.description}
          </Typography>
        </Box>
        
        <Card sx={{ p: 3 }}>
          <CardContent sx={{ p: 0 }}>

            {/* Informations détaillées */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.7rem',
                    display: 'block',
                    mb: 1
                  }}>
                    Statut
                  </Typography>
                  <Chip 
                    label={status || 'Non défini'} 
                    size="medium"
                    sx={{ 
                      backgroundColor: 'primary.light',
                      color: 'primary.dark',
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.7rem',
                    display: 'block',
                    mb: 1
                  }}>
                    Priorité
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.95rem'
                  }}>
                    {priority}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.7rem',
                    display: 'block',
                    mb: 1
                  }}>
                    Début
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.95rem'
                  }}>
                    {formatDate(taskDetail.workflowInstance?.startDate) || 'Non défini'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.7rem',
                    display: 'block',
                    mb: 1
                  }}>
                    Échéance
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 600,
                    color: taskDetail.properties?.bpm_dueDate ? 'text.primary' : 'text.secondary',
                    fontSize: '0.95rem'
                  }}>
                    {formatDate(taskDetail.properties?.bpm_dueDate) || 'Aucune'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderDocumentViewer = () => {
    if (documents.length === 0) {
      return (
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#254676' }}>
              📄 Documents
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ flexGrow: 1 }}>
              <Typography color="text.secondary">
                Aucun document disponible
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              mb: 2
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ color: '#254676', mb: documents.length > 1 ? 0.5 : 0 }}>
                📄 {selectedDocument ? selectedDocument.name : `Documents (${documents.length})`}
              </Typography>
              {documents.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  Document {selectedDocumentIndex + 1} / {documents.length}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {documents.length > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 999,
                    backgroundColor: '#f8fafc',
                    px: 1
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleDocumentNavigation('prev')}
                    aria-label="Document précédent"
                  >
                    <ArrowBackIosNewIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{ fontWeight: 600, width: 70, textAlign: 'center' }}>
                    {selectedDocumentIndex + 1} / {documents.length}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDocumentNavigation('next')}
                    aria-label="Document suivant"
                  >
                    <ArrowForwardIosIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              {selectedDocument && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                  sx={{
                    borderColor: '#254676',
                    color: '#254676',
                    '&:hover': {
                      borderColor: '#1a3554',
                      backgroundColor: '#f2ecea'
                    }
                  }}
                >
                  Modifier
                </Button>
              )}
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {/* Visualiseur de document */}
          {selectedDocument && (
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box 
                sx={{ 
                  flexGrow: 1, 
                  border: 1, 
                  borderColor: 'divider', 
                  borderRadius: 1,
                  overflow: 'hidden',
                  height: '100%',
                  minHeight: '400px'
                }}
              >
                {(() => {
                  const mimeType = selectedDocument.content?.mimeType || '';
                  const fileName = selectedDocument.name?.toLowerCase() || '';
                  
                  // Détecter d'abord les images (priorité sur PDF)
                  const isImage = mimeType.startsWith('image/') || 
                                 ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].some(ext => 
                                   fileName.endsWith(`.${ext}`)
                                 );
                  
                  // Détecter les PDF uniquement si ce n'est pas une image
                  const isPDF = !isImage && (mimeType.includes('pdf') || fileName.endsWith('.pdf'));
                  
                  if (isImage) {
                    return (
                      <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: '#f5f5f5',
                      overflow: 'auto',
                      p: 2
                    }}
                  >
                    <img
                      src={getDocumentPreviewUrl(selectedDocument)}
                      alt={selectedDocument.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        height: 'auto',
                        width: 'auto',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <Box
                      sx={{
                        display: 'none',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        p: 4
                      }}
                    >
                      <Typography variant="h6" color="text.secondary">
                        Erreur de chargement de l'image
                      </Typography>
                      <Button
                        variant="outlined"
                        href={getDocumentContentUrl(selectedDocument)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ borderColor: '#254676', color: '#254676' }}
                      >
                        Télécharger l'image
                      </Button>
                      </Box>
                    </Box>
                    );
                  } else if (isPDF) {
                    return (
                      <iframe
                        src={getDocumentPreviewUrl(selectedDocument)}
                        style={{ 
                          width: '100%', 
                          height: '100%',
                          border: 'none',
                          minHeight: '80vh'
                        }}
                        title={`Aperçu de ${selectedDocument.name}`}
                      />
                    );
                  } else {
                    return (
                      <Box 
                    display="flex" 
                    justifyContent="center" 
                    alignItems="center" 
                    height="100%"
                    flexDirection="column"
                    gap={2}
                  >
                    <Typography color="text.secondary">
                      Aperçu non disponible pour ce type de fichier
                    </Typography>
                    <Button
                      variant="outlined"
                      href={getDocumentContentUrl(selectedDocument)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Télécharger le document
                    </Button>
                      </Box>
                    );
                  }
                })()}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {renderHeaderInfo()}

      <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
        {/* Formulaire à gauche - 30% */}
        <Grid item xs={12} md={4}>
          <Card sx={{ boxShadow: 2, height: 'calc(100vh - 120px)', overflow: 'auto' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
                Formulaire de la tâche
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Box component="form" onSubmit={handleSubmit} sx={{ flexGrow: 1, overflow: 'auto' }}>
                {Object.keys(groupedFields).map((groupKey) => {
                  const group = FIELD_GROUPS[groupKey];
                  const fields = groupedFields[groupKey];
                  
                  if (!fields || fields.length === 0) return null;

                  return (
                    <Accordion key={groupKey} defaultExpanded sx={{ mb: 2 }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                          backgroundColor: '#f8f9fa',
                          '& .MuiAccordionSummary-content': {
                            alignItems: 'center'
                          }
                        }}
                      >
                        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                          <span style={{ marginRight: '8px', fontSize: '1.2em' }}>{group.icon}</span>
                          {group.title}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {groupKey === 'workflow' && renderCommentHistory()}
                        <Grid container spacing={2}>
                          {fields.map((field) => (
                            (groupKey === 'workflow' && field.id === 'cima_motif') ? null : (
                            <Grid 
                              item 
                              xs={12} 
                              key={field.id}
                            >
                              {renderField(field)}
                            </Grid>
                            )
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                {(() => {
                  const actionButtons = getTaskButtons();
                  const buttonCount = actionButtons.length;
                  if (buttonCount === 0) return null;

                  const justifyContent = buttonCount === 1
                    ? 'flex-end'
                    : buttonCount === 2
                      ? 'center'
                      : 'space-between';

                  const buttonFlex = buttonCount > 2 ? '1 1 calc(33% - 12px)' : '0 0 auto';
                  const minWidth = buttonCount > 2 ? 120 : 110;

                  return (
                    <Box sx={{ 
                      mt: 4, 
                      pt: 3, 
                      borderTop: '1px solid', 
                      borderColor: 'divider', 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 2
                    }}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          flexWrap: 'wrap',
                          justifyContent,
                          width: '100%'
                        }}
                      >
                        {actionButtons.map((button, index) => (
                          <Button
                            key={index}
                            variant="contained"
                            disabled={submitting}
                            onClick={(e) => handleSubmit(e, button.outcome)}
                            size="small"
                            color={button.color === 'success' ? 'success' : button.color === 'error' ? 'error' : button.color === 'warning' ? 'warning' : 'primary'}
                            sx={{ 
                              minWidth,
                              flex: buttonFlex
                            }}
                          >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : button.label}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Visualiseur de documents à droite - 70% */}
        <Grid item xs={12} md={8}>
          <Box sx={{ height: 'calc(100vh - 120px)' }}>
            {renderDocumentViewer()}
          </Box>
        </Grid>
      </Grid>

      {/* Popup d'upload de document */}
      {renderUploadDialog()}
    </Container>
  );
};

export default TaskDetail;

